import { useCallback, useSyncExternalStore } from 'react';
import { useRecoilValue } from 'recoil';
import { CrosshairInteractionMode } from '~/engine/tool/ToolManager';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { viewManagerState } from '~/state/atoms/engineAtoms';

/**
 * Subscribes to the viewer's tool state changes and returns the current
 * {@link CrosshairInteractionMode} for the given view: Full while the
 * viewer has no engine yet.
 *
 * Uses useSyncExternalStore for automatic deduplication —
 * the component only re-renders when the mode actually changes.
 */
export function useCrosshairInteraction(
    view: PlaneView | null
): CrosshairInteractionMode {
    const toolManager =
        useRecoilValue(viewManagerState)?.interactionContext.toolManager;
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const emitter = toolManager?.eventEmitter;
            emitter?.on('toolStateChanged', onStoreChange);
            return () => {
                emitter?.off('toolStateChanged', onStoreChange);
            };
        },
        [toolManager]
    );

    return useSyncExternalStore(
        subscribe,
        () =>
            toolManager?.getCrosshairInteractionMode(view) ??
            CrosshairInteractionMode.Full
    );
}
